import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export const POLICY_LINKS = [
  { to: "/policies", label: "Overview", icon: "ri-file-shield-2-line" },
  { to: "/policies/terms", label: "Terms of Service", icon: "ri-file-text-line" },
  { to: "/policies/cancellation", label: "Cancellation Policy", icon: "ri-calendar-close-line" },
  { to: "/policies/insurance", label: "Protection Plans", icon: "ri-shield-star-line" },
  { to: "/policies/guest-standards", label: "Guest Standards", icon: "ri-user-star-line" },
];

interface PolicyLayoutProps {
  eyebrow: string;
  title: string;
  intro: string;
  updated: string;
  children: ReactNode;
}

export default function PolicyLayout({ eyebrow, title, intro, updated, children }: PolicyLayoutProps) {
  const location = useLocation();

  return (
    <div className="min-h-screen w-full bg-background-50">
      <Navbar />

      <header className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 pb-12 pt-28 md:px-8 md:pb-16 md:pt-40">
          <div className="max-w-3xl">
            <span className="text-sm font-medium uppercase tracking-[0.2em] text-primary-500">{eyebrow}</span>
            <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">{title}</h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{intro}</p>
            <p className="mt-4 flex items-center gap-2 text-xs text-foreground-500">
              <i className="ri-history-line"></i> Last updated {updated}
            </p>
          </div>
        </div>
      </header>

      <div className="mx-auto w-full max-w-6xl px-4 pb-20 pt-10 md:px-8 md:pt-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-12">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">On this shelf</p>
            <nav className="mt-3 flex flex-col gap-1">
              {POLICY_LINKS.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
                      active
                        ? "bg-primary-500 font-medium text-background-50"
                        : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                    }`}
                  >
                    <i className={item.icon}></i>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>

          <article className="min-w-0 max-w-3xl space-y-9">{children}</article>
        </div>
      </div>

      <Footer />
    </div>
  );
}

interface PolicySectionProps {
  id: string;
  title: string;
  children: ReactNode;
}

export function PolicySection({ id, title, children }: PolicySectionProps) {
  return (
    <section id={id} className="scroll-mt-28">
      <h2 className="group flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950 md:text-xl">
        <a href={`#${id}`} className="flex items-center gap-2">
          {title}
          <i className="ri-link text-sm text-foreground-400 opacity-0 transition-opacity group-hover:opacity-100"></i>
        </a>
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-600">{children}</div>
    </section>
  );
}

export function PolicyList({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2.5">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2.5">
          <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-primary-500"></i>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PolicyCallout({ icon = "ri-information-line", children }: { icon?: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-accent-500/30 bg-accent-500/10 p-4">
      <i className={`${icon} mt-0.5 text-lg text-accent-500`}></i>
      <div className="text-sm leading-relaxed text-foreground-700">{children}</div>
    </div>
  );
}